import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import SectionShell from '@/components/feature/SectionShell';
import SectionHeader, { microCls } from '@/components/feature/SectionHeader';
import {
  studioApproach, studioProcess, studioBehindScenes, studioLocations, studioClients,
} from '@/mocks/studio';
import Reveal, { ArmedGroup, moDelay } from '@/lib/motion/Reveal';
import { playRouteFade } from '@/lib/route-fade';

const heroImage = 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/f767a23e-ce62-45b0-b505-e0d13e4d5847_studio-hero.webp';

export default function StudioPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Studio — Maren Voss Photography, Berlin & Lisbon';
  }, []);

  return (
    <PageShell>
      <SectionShell labelledBy="studio-page-title" className="!border-t-0">
        <SectionHeader as="h1" index="01" name="Studio" titleId="studio-page-title" title="Photography shaped by people, light and collaboration." meta="Berlin / Lisbon · Since 2016" className="!mb-0" />
        <Reveal as="p" variant="text" delay={160} className="mt-10 max-w-xl text-[15px] leading-relaxed text-foreground-700 md:mt-12">
          Maren Voss is a photography studio working across campaign, portrait and editorial commissions. The work is
          quiet in method and clear in result — real people, considered light, and images that feel like the brands they belong to.
        </Reveal>
      </SectionShell>

      <ArmedGroup kind="media" className="relative h-[420px] w-full overflow-hidden md:h-[70vh]">
        <img src={heroImage} alt="Daylight photography studio with crew at work" title="Maren Voss Photography — studio in Berlin" className="absolute inset-0 h-full w-full object-cover object-center" />
      </ArmedGroup>

      <SectionShell labelledBy="approach-title">
        <SectionHeader index="02" name="Approach" titleId="approach-title" title="How the work is made" />
        <ArmedGroup as="ul" className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-0">
          {studioApproach.map((a, i) => (
            <li key={a.id} style={moDelay(0, i, 70)} className={`mo-aitem mo--note md:px-8 ${i === 0 ? 'md:pl-0' : 'mo-rule'}`}>
              <h3 className="font-heading text-[22px] font-medium tracking-[-0.015em] text-foreground-950">{a.title}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-foreground-700">{a.text}</p>
            </li>
          ))}
        </ArmedGroup>
      </SectionShell>

      <SectionShell labelledBy="process-title" surface="subtle">
        <SectionHeader index="03" name="Process" titleId="process-title" title="From brief to delivery" meta="Typical timeline 2–5 weeks" />
        <ArmedGroup as="ol" className="flex flex-col gap-8 md:gap-10">
          {studioProcess.map((p, i) => (
            <li key={p.id} style={moDelay(0, i, 55)} className="mo-aitem mo--row grid grid-cols-1 gap-2 md:grid-cols-12 md:items-baseline md:gap-4">
              <span className="font-heading text-2xl text-foreground-500 md:col-span-1 md:text-[28px]">{p.index}</span>
              <h3 className="font-heading text-xl font-medium tracking-[-0.015em] text-foreground-950 md:col-span-5 md:text-[24px]">{p.title}</h3>
              <p className="text-[15px] leading-relaxed text-foreground-700 md:col-span-6">{p.text}</p>
            </li>
          ))}
        </ArmedGroup>
      </SectionShell>

      <SectionShell labelledBy="bts-title">
        <SectionHeader index="04" name="Behind the scenes" titleId="bts-title" title="On set" meta={`${studioBehindScenes.length} frames`} />
        <ArmedGroup className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {studioBehindScenes.map((b, i) => (
            <div key={b.id} style={moDelay(0, i, 70)} className="mo-aitem mo--block aspect-[3/4] w-full overflow-hidden bg-background-100">
              <img src={b.src} alt={b.alt} title={`${b.alt} — Maren Voss Photography`} className="h-full w-full object-cover object-top" />
            </div>
          ))}
        </ArmedGroup>
      </SectionShell>

      <SectionShell labelledBy="locations-title">
        <SectionHeader index="05" name="Locations" titleId="locations-title" title="Where we work" />
        <ArmedGroup as="ul" className="flex flex-col gap-5 md:gap-6">
          {studioLocations.map((l, i) => (
            <li key={l.id} style={moDelay(0, i, 40)} className="mo-aitem mo--rate grid grid-cols-1 items-baseline gap-2 md:grid-cols-12 md:gap-4">
              <span className="text-[17px] font-medium text-foreground-950 md:col-span-4">{l.city}</span>
              <span className="text-[15px] text-foreground-700 md:col-span-8">{l.detail}</span>
            </li>
          ))}
        </ArmedGroup>
        <Reveal variant="text" className="mt-16">
          <p className={`${microCls} text-foreground-500`}>Selected clients</p>
          <p className="mt-5 max-w-4xl font-heading text-[22px] font-normal leading-[1.4] tracking-[-0.01em] text-foreground-950 md:text-[26px]">
            {studioClients.join(' · ')}
          </p>
        </Reveal>
        <Link to="/contact" onClick={playRouteFade} className="mo-btn mo-btn--cta mt-14 inline-flex items-center gap-2 bg-foreground-950 px-8 py-4 font-label text-[12px] font-medium uppercase tracking-[0.12em] text-background-50 hover:opacity-90 whitespace-nowrap cursor-pointer">
          <span className="mo-uline">Start an inquiry</span> <span aria-hidden="true" className="mo-arrow">→</span>
        </Link>
      </SectionShell>
    </PageShell>
  );
}